/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements, max-lines-per-function, @typescript-eslint/no-magic-numbers, no-mixed-operators, complexity */
import { type ImageBackgroundThemeTyping } from '@shared/typings/ui';
export const hexToHsl = (hexColor: string): string => {
    // Remove '#' symbol if present
    const hex = hexColor.replace('#', '');

    // Convert hex to RGB
    const red = parseInt(hex.substring(0, 2), 16) / 255;
    const green = parseInt(hex.substring(2, 4), 16) / 255;
    const blue = parseInt(hex.substring(4, 6), 16) / 255;

    // Find the min and max values for RGB
    const min = Math.min(
        red, green, blue,
    );
    const max = Math.max(
        red, green, blue,
    );

    // Calculate the lightness (L)
    const lightness = (max + min) / 2;

    // Calculate the saturation (S)
    let saturation = 0;
    if (max === min) {
        saturation = 0;
    } else if (lightness < 0.5) {
        saturation = (max - min) / (max + min);
    } else {
        saturation = (max - min) / (2 - max - min);
    }

    // Calculate the hue (H)
    let hue = 0;
    if (max === min) {
        hue = 0;
    } else if (max === red) {
        hue = (green - blue) / (max - min);
    } else if (max === green) {
        hue = 2 + (blue - red) / (max - min);
    } else {
        hue = 4 + (red - green) / (max - min);
    }

    // Convert hue to degrees and round to 2 decimal places
    hue *= 60;
    if (hue < 0) {
        hue += 360;
    }
    hue = Math.round(hue * 100) / 100;

    // Format and return the HSL color
    return `hsl(${Math.round(hue)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%)`;
};

export const hslToHex = (hsl: string | string[]): string => {
    const sep = hsl.includes(',') ? ',' : ' ';
    // eslint-disable-next-line no-param-reassign, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    hsl = (hsl as string).substring(4)
        .split(')')[0].split(sep);

    let [hue]: string[] = hsl;
    const [saturation, lightness]: number[] = [
        parseFloat(hsl[1].substring(0, hsl[1].length - 1)) / 100,
        parseFloat(hsl[2].substring(0, hsl[2].length - 1)) / 100,
    ];

    // Strip label and convert to degrees (if necessary)
    if (hue.includes('deg')) {
        hue = hue.substring(0, hue.length - 3);
    } else if (hue.includes('rad')) {
        hue = Math.round(parseFloat(hue.substring(0, hue.length - 3)) * (180 / Math.PI))
            .toString();
    } else if (hue.includes('turn')) {
        hue = Math.round(parseFloat(hue.substring(0, hue.length - 4)) * 360)
            .toString();
    }

    const parsedHue = parseInt(hue);

    if (parsedHue >= 360) {
        hue = (parsedHue % 360).toString();
    }

    const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation,
        intermediate = chroma * (1 - Math.abs((parsedHue / 60) % 2 - 1)),
        lightnessModifier = lightness - chroma / 2;
    let red = 0,
        green = 0,
        blue = 0;

    if (parsedHue >= 0 && parsedHue < 60) {
        red = chroma;
        green = intermediate;
        blue = 0;
    } else if (parsedHue >= 60 && parsedHue < 120) {
        red = intermediate;
        green = chroma;
        blue = 0;
    } else if (parsedHue >= 120 && parsedHue < 180) {
        red = 0;
        green = chroma;
        blue = intermediate;
    } else if (parsedHue >= 180 && parsedHue < 240) {
        red = 0;
        green = intermediate;
        blue = chroma;
    } else if (parsedHue >= 240 && parsedHue < 300) {
        red = intermediate;
        green = 0;
        blue = chroma;
    } else if (parsedHue >= 300 && parsedHue < 360) {
        red = chroma;
        green = 0;
        blue = intermediate;
    }

    // Having obtained RGB, convert channels to hex
    const hexRed = Math.round((red + lightnessModifier) * 255)
        .toString(16)
        .padStart(2, '0');
    const hexGreen = Math.round((green + lightnessModifier) * 255)
        .toString(16)
        .padStart(2, '0');
    const hexBlue = Math.round((blue + lightnessModifier) * 255)
        .toString(16)
        .padStart(2, '0');

    return `#${hexRed}${hexGreen}${hexBlue}`;
};
/* eslint-enable max-statements, max-lines-per-function, @typescript-eslint/no-magic-numbers, no-mixed-operators, complexity */

const SAMPLE_SIZE = 50;
const LUMINANCE_THRESHOLD = 0.5;
const GAMMA_THRESHOLD = 0.03928;
const GAMMA_DIVISOR_LOW = 12.92;
const GAMMA_DIVISOR_HIGH = 1.055;
const GAMMA_OFFSET = 0.055;
const GAMMA_POWER = 2.4;
const LUMINANCE_WEIGHT_RED = 0.2126;
const LUMINANCE_WEIGHT_GREEN = 0.7152;
const LUMINANCE_WEIGHT_BLUE = 0.0722;
const MAX_COLOR_VALUE = 255;
const PIXEL_BYTE_STRIDE = 4;
const PIXEL_ALPHA_OFFSET = 3;

const toLinearRgb = (channel: number): number => {
    const normalized = channel / MAX_COLOR_VALUE;
    return normalized <= GAMMA_THRESHOLD
        ? normalized / GAMMA_DIVISOR_LOW
        : ((normalized + GAMMA_OFFSET) / GAMMA_DIVISOR_HIGH)**GAMMA_POWER;
};

/**
 * Analyzes the dominant brightness of an image and returns whether a 'dark' or 'light'
 * background should be used in the container to ensure visual contrast.
 *
 * - Returns 'dark'  when the image is bright (high average luminance)
 * - Returns 'light' when the image is dark  (low average luminance)
 *
 * Transparent pixels are excluded from the luminance calculation.
 * Falls back to 'light' on load error or when no opaque pixels are found.
 */
// eslint-disable-next-line max-lines-per-function
export const getImageBackgroundTheme = (imageUrl: string): Promise<ImageBackgroundThemeTyping> =>
    // eslint-disable-next-line max-lines-per-function
    new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        // eslint-disable-next-line max-statements, max-lines-per-function
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = SAMPLE_SIZE;
            canvas.height = SAMPLE_SIZE;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                resolve('light');
                return;
            }

            ctx.drawImage(
                img, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE,
            );
            const { data: dataImg } = ctx.getImageData(
                0, 0, SAMPLE_SIZE, SAMPLE_SIZE,
            );

            let totalLuminance = 0;
            let pixelCount = 0;

            for (let i = 0; i < dataImg.length; i += PIXEL_BYTE_STRIDE) {
                // Skip fully transparent pixels
                if (dataImg[i + PIXEL_ALPHA_OFFSET] !== 0) {
                    const luminance
                        = (LUMINANCE_WEIGHT_RED * toLinearRgb(dataImg[i]))
                        + (LUMINANCE_WEIGHT_GREEN * toLinearRgb(dataImg[i + 1]))
                        + (LUMINANCE_WEIGHT_BLUE * toLinearRgb(dataImg[i + 2]));

                    totalLuminance += luminance;
                    pixelCount += 1;
                }
            }

            if (pixelCount === 0) {
                resolve('light');
                return;
            }

            const averageLuminance = totalLuminance / pixelCount;
            resolve(averageLuminance > LUMINANCE_THRESHOLD ? 'dark' : 'light');
        };

        img.onerror = () => { resolve('light') };
        img.src = imageUrl;
    });
